/**
 * Plan Access Checker
 *
 * Pure utility functions for checking tenant plan status.
 * Works in both browser and Cloudflare Worker environments.
 *
 * These functions work with the raw tenant data fields, not the
 * pre-computed planStatus/usageStatus that the API returns.
 */

import type { PlanStatus, UsageStatus } from '../api'
import type { SeatCounts } from '../entities'
import {
  DEFAULT_TRIAL_DAYS,
  PRO_EXPIRING_SOON_DAYS,
  MS_PER_DAY,
} from './constants'
import { templateBaseWithoutPro } from './entitlements'

/**
 * Minimal tenant interface for plan checking.
 * Works with both frontend Tenant type and backend Selectable<TenantsTable>
 */
export interface TenantPlanFields {
  plan: 'free' | 'pro'
  plan_expires_at: number | null
  trial_started_at: number | null
  monthly_quota: number | null
  current_quota: number | null
  quota_reset_day_number: number | null
  enforce_quota: number
  max_templates: number
  // Optional: paid Extra Template add-ons, which survive plan expiry (treated as none if omitted)
  extra_template_count?: number
  // Optional: seats in force, which also survive plan expiry. Set by
  // entitlementPlanFields(); a raw row has none and reads as no seats.
  seats?: SeatCounts
}

/**
 * How many templates a tenant may keep *live* right now.
 *
 * `max_templates` is the allowance their plan grants while it is valid; it is
 * deliberately never downgraded automatically, so it still reflects what they
 * had (and what they get back on renewal). Once the plan lapses the live
 * allowance falls to the base they hold without it — the Free tier, or the Pro
 * base if any seat is in force — plus any Extra Template add-ons they are still
 * paying for. Seats and add-ons are separate subscriptions and outlive the plan.
 *
 * Enforcement is reversible: templates above this number are disabled, not
 * deleted, so renewing restores them.
 */
export function getEffectiveMaxTemplates(
  tenant: Pick<TenantPlanFields, 'plan' | 'plan_expires_at' | 'trial_started_at' | 'max_templates'> & {
    extra_template_count?: number
    seats?: SeatCounts
  },
  now: number = Date.now(),
): number {
  const isExpired = tenant.plan_expires_at !== null && now >= tenant.plan_expires_at
  if (!isExpired) return tenant.max_templates
  return templateBaseWithoutPro(tenant.seats) + (tenant.extra_template_count ?? 0)
}

/**
 * Whether the generation quota can bind this tenant at all right now.
 *
 * Three things exempt a *tenant*: Pro access (paid Pro or active trial), an
 * explicitly unlimited `monthly_quota`, and `enforce_quota = 0` ("tracking
 * only"). This is the tenant-wide half of the gate; a single run can also be
 * exempt on its own account — a seat, the automation allowance — which is
 * `resolveCharge()` in quota.ts, and that function calls this one.
 *
 * Everything that displays quota must ask this rather than reading the columns
 * directly — the admin screens once grew their own copy of this predicate and
 * drifted from the gate.
 */
export function isQuotaEnforced(
  tenant: Pick<
    TenantPlanFields,
    'plan' | 'plan_expires_at' | 'trial_started_at' | 'monthly_quota' | 'enforce_quota'
  >,
  now: number = Date.now(),
): boolean {
  if (tenant.enforce_quota !== 1) return false
  if (tenant.monthly_quota === null) return false
  return !hasProAccess(tenant as TenantPlanFields, now)
}

export interface SubscriptionSnapshot {
  quantity: number
  /** Unix ms when a cancel-at-period-end takes effect, else null. */
  scheduled_cancel_at: number | null
}

/** Which operator cards a subscription event should produce. */
export interface SubscriptionTransitions {
  /** A new subscription or a genuine quantity increase — i.e. money was spent. */
  purchase: boolean
  /** A cancel-at-period-end was just scheduled (null -> date). */
  cancelJustScheduled: boolean
}

/**
 * Decide which operator notifications a Paddle subscription event warrants.
 *
 * Must be edge-triggered, not state-triggered. Paddle emits
 * `subscription.updated` for renewals, payment-method changes and past_due
 * recovery, and keeps `scheduled_change` populated until it takes effect — so
 * notifying on raw state would fire a churn card every month and make every
 * renewal look like a fresh sale.
 *
 * `eventType` is part of the decision, not a detail. A first purchase emits
 * `subscription.created` AND `subscription.activated` within milliseconds of
 * each other; both handlers read the stored row before either has written, so
 * both see "no previous row" and a quantity comparison alone fires twice.
 * Observed in staging 23ms apart. Only `created` is treated as the first
 * purchase, so the concurrent `activated` cannot duplicate it.
 *
 * @param previous - the stored row, or null when this subscription is new
 */
export function subscriptionTransitions(
  eventType: string,
  previous: SubscriptionSnapshot | null,
  next: SubscriptionSnapshot,
): SubscriptionTransitions {
  const quantityIncreased = next.quantity > (previous?.quantity ?? 0)
  return {
    purchase:
      eventType === 'subscription.created' ||
      (eventType === 'subscription.updated' && quantityIncreased),
    // Scheduling a cancellation arrives as an update; restricting the event type
    // keeps a concurrent activated/created from racing this the same way.
    cancelJustScheduled:
      eventType === 'subscription.updated' &&
      next.scheduled_cancel_at !== null &&
      (previous?.scheduled_cancel_at ?? null) === null,
  }
}

/** What a Paddle adjustment should do to the tenant. */
export interface AdjustmentEffect {
  /** Take the document packs the adjusted transaction bought out of the balance. */
  reverseCredits: boolean
  /** Put back packs a previous chargeback took. */
  restoreCredits: boolean
  /** Bar self-serve purchasing until an operator clears it. */
  blockBilling: boolean
  /** Lift a previous billing block. */
  unblockBilling: boolean
}

const NO_EFFECT: AdjustmentEffect = {
  reverseCredits: false,
  restoreCredits: false,
  blockBilling: false,
  unblockBilling: false,
}

/**
 * Decide what a Paddle adjustment does to a tenant's entitlement.
 *
 * The refund/chargeback split is the whole point of this function, and the two
 * are deliberately not symmetric:
 *
 * - A **refund** is something we chose to grant, under a policy that only
 *   refunds packs that are untouched. It is gated on `approved` because Paddle
 *   creates most live refunds as `pending_approval` and reviews them — acting
 *   on creation would take the pack for a refund that is then rejected, leaving
 *   a customer who paid short of what they bought.
 *
 * - A **chargeback** is not ours to grant. The money is already gone, so the
 *   entitlement goes with it regardless of how much has been consumed and
 *   regardless of status: there is no version of a chargeback where the
 *   customer keeps both the credits and the payment.
 *
 * `chargeback_warning` is an early notice that a dispute may be coming, not a
 * movement of money. It notifies only — blocking on a warning would punish
 * customers over inquiries that are frequently withdrawn.
 *
 * `credit` / `credit_reverse` reduce or restore the balance owed on an *unpaid*
 * transaction. No cash moves and nothing was delivered against a payment, so
 * they carry no entitlement change.
 */
export function adjustmentEffect(action: string, status: string): AdjustmentEffect {
  switch (action) {
    case 'refund':
      if (status === 'approved') return { ...NO_EFFECT, reverseCredits: true }
      // An approved refund that is later set back to 'reversed' was undone, so
      // the credits must come back — otherwise the customer keeps neither the
      // money nor the entitlement. Note this does NOT unblock billing: a refund
      // never blocked it, and clearing the flag here would lift a block a
      // separate chargeback had set.
      if (status === 'reversed') return { ...NO_EFFECT, restoreCredits: true }
      // Note the asymmetry with `chargeback` below, which also restores on
      // 'rejected'. A refund only ever reverses once *approved*, so 'rejected'
      // here means it was declined before anything was taken — there is nothing
      // to give back. A chargeback acts while still pending, so for it
      // 'rejected' genuinely means "undo what we already did".
      return NO_EFFECT

    case 'chargeback':
      // Acting while still pending is deliberate — the money is already gone —
      // but that makes the reversal provisional, so any outcome in our favour
      // must undo it. Nothing else will: `chargeback_reverse` is a separate
      // action Paddle does not emit for a chargeback that simply ended as
      // rejected or reversed, so without this the credits stay gone and the
      // tenant stays blocked forever.
      return status === 'rejected' || status === 'reversed'
        ? { ...NO_EFFECT, restoreCredits: true, unblockBilling: true }
        : { ...NO_EFFECT, reverseCredits: true, blockBilling: true }

    case 'chargeback_reverse':
      // A rejected reversal is not a reversal: the chargeback still stands, so
      // the customer keeps the money and must not also get the credits back.
      // countOpenDisputes() takes the same view — these two must agree, or the
      // block is held while the balance is quietly restored.
      return status === 'rejected'
        ? NO_EFFECT
        : { ...NO_EFFECT, restoreCredits: true, unblockBilling: true }

    default:
      // chargeback_warning, credit, credit_reverse, and anything Paddle adds
      // later: recorded and notified, but never silently acted upon.
      return NO_EFFECT
  }
}

/**
 * Whether an adjustment covers its transaction in full.
 *
 * Paddle's dashboard refunds per line item and per amount, and an adjustment
 * reports only money — never documents. A transaction's packs are reversed
 * together or not at all, so a partial refund cannot be apportioned: reversing
 * them would strip every document for a fraction of the money back. So
 * partials are not applied automatically at all; they go to an operator.
 *
 * Conservative by construction — an adjustment we cannot prove is full is
 * treated as partial. `tax` items are ignored (they accompany a refund rather
 * than describing its extent); `partial` and `proration` items disqualify it.
 */
export function isFullAdjustment(items: Array<{ type?: string }> | null | undefined): boolean {
  if (!items || items.length === 0) return false
  if (items.some((i) => i.type === 'partial' || i.type === 'proration')) return false
  return items.some((i) => i.type === 'full')
}

/**
 * Render a Paddle money amount for display.
 *
 * Paddle reports amounts in the currency's **minor units** — a $32.19 refund
 * arrives as the string "3219". Printing that raw on an operator card reads as
 * $3,219, overstating the amount 100-fold on exactly the message someone acts
 * on. (Observed on the first live sandbox refund.)
 *
 * The exponent is per-currency, not always 2 — JPY has none, so "666" there is
 * ¥666, not ¥6.66. Intl knows the right value, so it is asked rather than
 * assumed. Returns null when there is nothing to show, and falls back to the
 * raw pair if the currency code is one Intl rejects.
 */
export function formatPaddleAmount(
  minorUnits: string | null | undefined,
  currencyCode: string | null | undefined,
): string | null {
  if (minorUnits === null || minorUnits === undefined || minorUnits === '') return null
  const amount = Number(minorUnits)
  if (!Number.isFinite(amount)) return null

  const code = (currencyCode || 'USD').toUpperCase()
  const exponent = PADDLE_CURRENCY_EXPONENTS[code] ?? 2

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(amount / Math.pow(10, exponent))
  } catch {
    return `${minorUnits} ${code}`
  }
}

/**
 * Minor-unit exponents for the non-2-decimal currencies Paddle supports.
 *
 * Deliberately an explicit table rather than asking Intl. Intl reports CLDR
 * *display* digits, which are not Paddle's minor-unit exponent — it renders
 * TWD with 2 decimals, so "3219" formatted that way reads NT$32.19 when Paddle
 * means NT$3,219, a 100× under-report on an operator card. Worse, ICU data
 * differs between Node and workerd, so the same input formatted differently in
 * tests and in production.
 *
 * Everything absent from this table is 2, which covers USD/EUR/GBP and the rest.
 */
const PADDLE_CURRENCY_EXPONENTS: Record<string, number> = {
  // Zero-decimal
  JPY: 0,
  KRW: 0,
  TWD: 0,
  CLP: 0,
  ISK: 0,
  HUF: 0,
  UGX: 0,
  VND: 0,
  // Three-decimal
  BHD: 3,
  JOD: 3,
  KWD: 3,
}

/** A `paddle_adjustments` row, reduced to what dispute counting needs. */
export interface DisputeRow {
  paddle_transaction_id: string
  action: string
  status: string
}

/**
 * How many of a tenant's chargebacks are still unresolved.
 *
 * `billing_blocked` must be set while any dispute is live and cleared only when
 * none is — so both decisions read this one number.
 *
 * Resolution arrives two different ways and both have to count:
 *  - Paddle updates the chargeback's own row to `rejected` / `reversed`; or
 *  - a separate `chargeback_reverse` adjustment lands, with its own id and
 *    action, leaving the original row untouched.
 *
 * The second case is why this is derived rather than read off a status column.
 * Stamping the chargeback rows as resolved instead would be simpler, but a
 * `chargeback_reverse` names only a transaction — so with two disputes on one
 * transaction it would close out both, unblocking a tenant whose second dispute
 * is still live, and would falsify the audit trail those rows exist to provide.
 *
 * Reversals are netted per transaction and clamped at zero, so a stray extra
 * reverse cannot mask an unrelated open dispute elsewhere.
 */
export function countOpenDisputes(rows: DisputeRow[]): number {
  const RESOLVED = ['rejected', 'reversed']
  const netByTransaction = new Map<string, number>()

  for (const row of rows) {
    const current = netByTransaction.get(row.paddle_transaction_id) ?? 0
    if (row.action === 'chargeback' && !RESOLVED.includes(row.status)) {
      netByTransaction.set(row.paddle_transaction_id, current + 1)
    } else if (row.action === 'chargeback_reverse' && row.status !== 'rejected') {
      netByTransaction.set(row.paddle_transaction_id, current - 1)
    }
  }

  let open = 0
  for (const net of netByTransaction.values()) open += Math.max(0, net)
  return open
}

/** The `document_packs` fields the reversal decision depends on. */
export interface PackReversalState {
  reversed_at: number | null
  reversed_by_adjustment_id: string | null
}

/**
 * What an adjustment should do to the packs a transaction bought.
 * - `reverse` / `restore` — take the packs out of the balance, or put them back
 * - `skip_partial` — a partial refund, which has no correct automatic split
 * - `none` — already applied, nothing to undo, or not ours to act on
 */
export type PackReversalAction = 'none' | 'reverse' | 'skip_partial' | 'restore'

/**
 * Decide what one adjustment does to the packs of one transaction.
 *
 * Extracted from the webhook because this is the part that has been wrong
 * twice: the states that matter are the intermediate ones, and they are
 * unreachable from an integration test without a live Paddle account.
 *
 * A transaction's packs move together — one refund covers the transaction, not
 * a pack — so `pack` is any one of them; they share their reversal state.
 *
 * Reversing never touches `remaining`. Documents already drawn stay drawn: the
 * published policy only refunds untouched packs, but nothing stops a goodwill
 * refund on a part-used one, and a chargeback ignores the policy entirely. The
 * top-up this replaced had to clamp a deduction at the balance to survive those
 * cases; here there is nothing to clamp, because nothing is deducted.
 *
 * @param reversedByAction - the `action` of the adjustment that performed the
 *   existing reversal (looked up via `pack.reversed_by_adjustment_id`), or
 *   null if nothing has reversed these packs.
 */
export function packReversalDecision(params: {
  pack: PackReversalState | null | undefined
  adjustmentId: string
  action: string
  status: string
  items: Array<{ type?: string }> | null | undefined
  reversedByAction: string | null
}): PackReversalAction {
  const { pack, adjustmentId, action, status, items, reversedByAction } = params
  if (!pack) return 'none'

  const effect = adjustmentEffect(action, status)

  if (effect.reverseCredits) {
    // Already taken. `reversed_at` covers the created/updated double-delivery
    // that the event-id claim cannot.
    if (pack.reversed_at !== null) return 'none'
    // This adjustment's reversal was already undone — a later re-delivery of it
    // must not take the pack a second time. Paddle retries for 3 days.
    if (pack.reversed_by_adjustment_id === adjustmentId) return 'none'
    // Conservative only for refunds; for a chargeback the money is gone either
    // way, so an unprovable payload must still reverse. See isFullAdjustment().
    if (action === 'refund' && !isFullAdjustment(items)) return 'skip_partial'
    return 'reverse'
  }

  if (effect.restoreCredits) {
    if (pack.reversed_at === null) return 'none'
    // The restoring adjustment must be the COUNTERPART of the reversal, not
    // merely any adjustment that happens to carry restoreCredits. Two shapes
    // qualify:
    //  1. a chargeback_reverse undoing a chargeback's reversal;
    //  2. the same adjustment that took the pack now being undone itself.
    //
    // Checking only "a chargeback did the reversal" was too loose: an unrelated
    // refund reported as `reversed` on the same transaction would then restore
    // the pack while the chargeback still stood, leaving the customer holding
    // both the money and the entitlement.
    const undoingOwnReversal = pack.reversed_by_adjustment_id === adjustmentId
    const isChargebackCounterpart = action === 'chargeback_reverse' && reversedByAction === 'chargeback'
    if (!undoingOwnReversal && !isChargebackCounterpart) return 'none'
    return 'restore'
  }

  return 'none'
}

/**
 * The expiry a pack gets back when its reversal is undone.
 *
 * The clock is paused for as long as the pack was out of the balance. A dispute
 * can take months to settle, and a customer who won it would otherwise get back
 * a pack with those months gone — or one already expired, which is worth
 * nothing. They paid for a period of validity; they get the rest of it.
 */
export function restoredPackExpiry(
  pack: { expires_at: number; reversed_at: number | null },
  now: number = Date.now(),
): number {
  if (pack.reversed_at === null) return pack.expires_at
  return pack.expires_at + Math.max(0, now - pack.reversed_at)
}

/**
 * Get detailed plan status - single source of truth for all plan logic
 *
 * @param tenant - Tenant data with plan fields
 * @param now - Optional timestamp for testing (defaults to Date.now())
 */
export function getPlanStatus(tenant: TenantPlanFields, now: number = Date.now()): PlanStatus {
  const isExpired = tenant.plan_expires_at !== null && now >= tenant.plan_expires_at

  // Determine effective plan type
  let planType: 'free' | 'trial' | 'pro'
  if (isExpired) {
    planType = 'free' // Expired trial/pro falls back to free
  } else if (tenant.plan === 'pro') {
    planType = 'pro'
  } else if (tenant.trial_started_at) {
    planType = 'trial'
  } else {
    planType = 'free'
  }

  const daysRemaining =
    tenant.plan_expires_at && !isExpired
      ? Math.ceil((tenant.plan_expires_at - now) / MS_PER_DAY)
      : 0

  return {
    planType,
    storedPlan: tenant.plan,
    isExpired,
    isProExpired: tenant.plan === 'pro' && isExpired,
    isProExpiringSoon: planType === 'pro' && !isExpired && tenant.plan_expires_at !== null && daysRemaining < PRO_EXPIRING_SOON_DAYS,
    trialPastHalfway: planType === 'trial' && !isExpired && daysRemaining <= DEFAULT_TRIAL_DAYS / 2,
    expiresAt: tenant.plan_expires_at,
    daysRemaining,
    hasAccess: planType !== 'free',
    trialStartedAt: tenant.trial_started_at,
    canStartTrial: !tenant.trial_started_at && planType === 'free',
  }
}

/**
 * Check if tenant has Pro access (subscription or active trial)
 *
 * Takes `now` so callers that already have a reference time — quota settlement,
 * usage reporting, tests — evaluate expiry against the same clock they use for
 * everything else. Defaulting to Date.now() here while the caller works from a
 * different `now` silently mixes two clocks in one calculation.
 */
export function hasProAccess(tenant: TenantPlanFields, now: number = Date.now()): boolean {
  return getPlanStatus(tenant, now).hasAccess
}

/**
 * Check if plan is expiring soon (for warning banners)
 *
 * @param planStatus - Pre-computed plan status
 * @param thresholdDays - Number of days to consider "soon"
 */
export function isExpiringSoon(planStatus: PlanStatus, thresholdDays: number): boolean {
  return (
    !planStatus.isExpired &&
    planStatus.expiresAt !== null &&
    planStatus.daysRemaining < thresholdDays
  )
}

/**
 * Check if quota usage is at warning level.
 *
 * Reads the flag getUsageStatus() already set from resolveCharge() — the gate's
 * own function, asked for the caller — rather than re-deriving it from the
 * tenant-wide numbers. This used to recompute, which with seats would warn a
 * seated user about a limit that cannot stop them. `thresholdPercent` can only
 * make the warning stricter than the status's own QUOTA_WARNING_THRESHOLD:
 * honouring a lower one would need that re-derivation back.
 *
 * @param usageStatus - Pre-computed usage status
 * @param thresholdPercent - Threshold as decimal (0.8 = 80%)
 */
export function isQuotaWarning(usageStatus: UsageStatus, thresholdPercent: number): boolean {
  return usageStatus.isQuotaWarning && usageStatus.quotaPercentUsed >= thresholdPercent * 100
}

/**
 * Check if quota is reached (hard limit) — the flag getUsageStatus() set from
 * the gate's own function, for the caller. See isQuotaWarning() for why it is
 * read, not recomputed.
 */
export function isQuotaReached(usageStatus: UsageStatus): boolean {
  return usageStatus.isQuotaReached
}
